import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { progressAPI } from '../../api/client'

const H = ({ style, ...p }: React.HTMLAttributes<HTMLElement>) => (
  <span style={{ fontFamily: "'Space Grotesk', sans-serif", ...style }} {...p} />
)

export default function RoadmapPage() {
  const navigate = useNavigate()

  const { data: plan, isLoading } = useQuery({
    queryKey: ['roadmap-plan'],
    queryFn: () => progressAPI.plan(),
  })

  const skillColors = {
    listening: { color: '#006571', bg: '#e6fafb', icon: 'headphones' },
    reading:   { color: '#004be2', bg: '#e8eeff', icon: 'menu_book' },
    writing:   { color: '#8e00d5', bg: '#f5e6ff', icon: 'edit_note' },
    speaking:  { color: '#b41340', bg: '#ffe8ed', icon: 'mic' },
  }

  // Build milestones from real plan data. If user hasn't taken the diagnostic
  // (no profile yet), milestones stay empty and we show a CTA instead.
  const CEFR_ORDER = ['A2', 'B1', 'B2', 'C1']
  const currentLevel: string | null = plan?.currentLevel ?? null
  const targetLevel: string | null = plan?.targetLevel ?? null
  const hasProfile = plan?.hasProfile !== false && (currentLevel || targetLevel)

  // Pick the level used as "current" cursor on the track. If only target exists
  // (user set goal but no diagnostic yet), treat the level before target as start.
  const cursorLevel = currentLevel
    ?? (targetLevel ? CEFR_ORDER[Math.max(0, CEFR_ORDER.indexOf(targetLevel) - 1)] : null)
  const cursorIdx = cursorLevel ? CEFR_ORDER.indexOf(cursorLevel) : -1

  const milestones = CEFR_ORDER.map((lvl, i) => ({
    level: lvl,
    done: cursorIdx >= 0 && i < cursorIdx,
    current: cursorIdx >= 0 && i === cursorIdx,
  }))

  return (
    <div style={{ minHeight: '100vh', background: '#f6f6ff', fontFamily: "'Manrope',sans-serif", color: '#272e42', position: 'relative', overflow: 'hidden' }}>
      {/* Blobs */}
      <div style={{ position: 'fixed', top: '-8rem', right: '-8rem', width: '30rem', height: '30rem', borderRadius: '50%', background: 'rgba(0,75,226,0.05)', filter: 'blur(100px)', pointerEvents: 'none' }} />

      {/* Top nav */}
      <nav style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem 2rem', background: 'rgba(255,255,255,0.65)', backdropFilter: 'blur(24px)', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ width: '2.25rem', height: '2.25rem', borderRadius: '0.75rem', background: 'linear-gradient(135deg,#004be2,#809bff)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span className="material-symbols-outlined" style={{ color: 'white', fontVariationSettings: "'FILL' 1", fontSize: '1.25rem' }}>bolt</span>
          </div>
          <H style={{ fontWeight: 800, fontSize: '1.25rem', letterSpacing: '-0.03em', color: '#004be2' }}>EngHustle</H>
        </div>
        <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#535b71' }}>Lộ trình cá nhân hoá</span>
      </nav>

      <main style={{ maxWidth: '60rem', margin: '0 auto', padding: '3rem 2rem' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 1rem', borderRadius: '9999px', background: 'rgba(142,0,213,0.08)', marginBottom: '1.25rem' }}>
            <span className="material-symbols-outlined" style={{ color: '#8e00d5', fontSize: '1rem', fontVariationSettings: "'FILL' 1" }}>route</span>
            <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8e00d5' }}>Lộ trình 90 ngày</span>
          </div>
          <H style={{ display: 'block', fontSize: 'clamp(2rem,4vw,3rem)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '0.75rem' }}>
            Hành trình của bạn<br />bắt đầu từ đây.
          </H>
          <p style={{ color: '#535b71', fontSize: '1.0625rem', lineHeight: 1.6 }}>
            {hasProfile
              ? 'AI đã phân tích kết quả và tạo kế hoạch tối ưu riêng cho bạn.'
              : 'Bạn chưa có lộ trình. Hãy làm bài đánh giá đầu vào để AI xây kế hoạch phù hợp.'}
          </p>
        </div>

        {!hasProfile && (
          <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', marginBottom: '2rem', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', textAlign: 'center' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '2.5rem', color: '#8e00d5', fontVariationSettings: "'FILL' 1", marginBottom: '0.75rem', display: 'inline-block' }}>quiz</span>
            <H style={{ display: 'block', fontSize: '1.25rem', fontWeight: 800, marginBottom: '0.5rem' }}>Chưa xác định trình độ của bạn</H>
            <p style={{ color: '#535b71', fontSize: '0.9375rem', marginBottom: '1.5rem' }}>
              Làm bài kiểm tra đầu vào (~5 phút) để biết trình độ CEFR hiện tại và nhận lộ trình cá nhân hoá.
            </p>
            <button
              onClick={() => navigate('/onboarding')}
              style={{ padding: '0.875rem 2rem', borderRadius: '9999px', border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg,#004be2,#809bff)', color: 'white', fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: '0.9375rem', boxShadow: '0 8px 24px rgba(0,75,226,0.25)' }}
            >
              Làm bài đánh giá ngay
            </button>
          </div>
        )}

        {/* CEFR Milestone tracker */}
        <div style={{ background: 'white', borderRadius: '2rem', padding: '2.5rem', marginBottom: '2rem', boxShadow: '0 10px 30px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '3rem' }}>
            <div>
              <H style={{ display: 'block', fontSize: '1.375rem', fontWeight: 800, marginBottom: '0.25rem' }}>Lộ trình CEFR</H>
              <span style={{ fontSize: '0.8125rem', color: '#535b71' }}>Tiến độ theo chuẩn quốc tế</span>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontSize: '0.625rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8e00d5', display: 'block' }}>Mục tiêu</span>
              <H style={{ fontSize: '1.375rem', fontWeight: 900, color: '#004be2' }}>
                {targetLevel ?? '—'}
              </H>
            </div>
          </div>

          {/* Neural nodes */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 2rem', marginBottom: '2rem', position: 'relative' }}>
            {/* Track line */}
            <div style={{ position: 'absolute', left: '2rem', right: '2rem', top: '50%', height: '3px', background: '#d9e2ff', transform: 'translateY(-50%)', zIndex: 0 }} />
            <div style={{ position: 'absolute', left: '2rem', width: `calc((100% - 4rem) * ${cursorIdx > 0 ? cursorIdx / (CEFR_ORDER.length - 1) : 0})`, top: '50%', height: '3px', background: 'linear-gradient(90deg,#004be2,#809bff)', transform: 'translateY(-50%)', zIndex: 1 }} />

            {milestones.map((m, i) => (
              <div key={m.level} style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.875rem' }}>
                <div style={{
                  width: m.current ? '4rem' : '2.75rem',
                  height: m.current ? '4rem' : '2.75rem',
                  borderRadius: '50%',
                  background: m.done || m.current ? 'linear-gradient(135deg,#004be2,#809bff)' : '#d9e2ff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: m.done || m.current ? 'white' : '#a5adc6',
                  fontFamily: "'Space Grotesk',sans-serif", fontWeight: 900, fontSize: m.current ? '1.25rem' : '0.875rem',
                  boxShadow: m.current ? '0 0 20px rgba(142,0,213,0.4), 0 0 0 6px rgba(0,75,226,0.1)' : 'none',
                  outline: m.current ? '3px solid white' : 'none',
                  position: 'relative',
                }}>
                  {m.done && !m.current ? (
                    <span className="material-symbols-outlined" style={{ fontSize: '1rem', fontVariationSettings: "'FILL' 1" }}>check</span>
                  ) : m.current ? m.level : (
                    <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>lock</span>
                  )}
                  {m.current && (
                    <div style={{ position: 'absolute', top: '-0.25rem', right: '-0.25rem', width: '0.875rem', height: '0.875rem', borderRadius: '50%', background: '#8e00d5', animation: 'pulse 2s ease-in-out infinite' }} />
                  )}
                </div>
                <H style={{ fontSize: m.current ? '0.875rem' : '0.75rem', fontWeight: m.current ? 900 : 700, color: m.current ? '#004be2' : '#a5adc6' }}>
                  {m.current ? 'Hiện tại' : m.level}
                </H>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '1rem' }}>
            <div style={{ padding: '1.25rem', background: '#eef0ff', borderRadius: '1rem' }}>
              <span style={{ fontSize: '0.625rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#535b71', display: 'block', marginBottom: '0.5rem' }}>XP còn lại</span>
              <H style={{ fontSize: '1.75rem', fontWeight: 900, color: '#272e42' }}>
                {plan?.remainingXP?.toLocaleString() ?? '1,420'} <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#535b71' }}>XP</span>
              </H>
            </div>
            <div style={{ padding: '1.25rem', background: '#eef0ff', borderRadius: '1rem' }}>
              <span style={{ fontSize: '0.625rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#535b71', display: 'block', marginBottom: '0.5rem' }}>Tốc độ trung bình</span>
              <H style={{ fontSize: '1.75rem', fontWeight: 900, color: '#8e00d5' }}>
                {plan?.dailyMinutes ?? 15} <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#535b71' }}>phút/ngày</span>
              </H>
            </div>
          </div>
        </div>

        {/* Skill recommendations */}
        {isLoading ? (
          <div style={{ background: 'white', borderRadius: '2rem', padding: '3rem', textAlign: 'center' }}>
            <div className="spinner" style={{ margin: '0 auto' }} />
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(11rem,1fr))', gap: '1rem', marginBottom: '2rem' }}>
            {Object.entries(skillColors).map(([skill, cfg]) => (
              <div key={skill} style={{ background: 'white', borderRadius: '1.5rem', padding: '1.5rem', boxShadow: '0 8px 24px rgba(0,0,0,0.04)', textAlign: 'center' }}>
                <div style={{ width: '3rem', height: '3rem', borderRadius: '1rem', background: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                  <span className="material-symbols-outlined" style={{ color: cfg.color, fontSize: '1.5rem', fontVariationSettings: "'FILL' 1" }}>{cfg.icon}</span>
                </div>
                <H style={{ display: 'block', fontSize: '0.875rem', fontWeight: 800, textTransform: 'capitalize', marginBottom: '0.25rem' }}>{skill}</H>
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: cfg.color, padding: '0.25rem 0.625rem', background: cfg.bg, borderRadius: '9999px' }}>
                  {plan?.skillFocus?.[skill] ?? 'Cân bằng'}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <div style={{ background: 'linear-gradient(135deg,#004be2,#809bff)', borderRadius: '2rem', padding: '3rem', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', bottom: '-2rem', right: '-2rem', width: '12rem', height: '12rem', background: 'rgba(142,0,213,0.3)', borderRadius: '50%', filter: 'blur(40px)' }} />
          <H style={{ display: 'block', fontSize: '1.75rem', fontWeight: 900, color: 'white', marginBottom: '0.75rem', position: 'relative' }}>
            Sẵn sàng hustle chưa? 🚀
          </H>
          <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '2rem', position: 'relative' }}>
            Lộ trình của bạn đã sẵn sàng. AI sẽ điều chỉnh theo tiến độ học mỗi ngày.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              padding: '1.125rem 3rem', borderRadius: '9999px', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.75rem',
              background: 'white', color: '#004be2', fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800, fontSize: '1rem',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)', transition: 'all 0.2s', position: 'relative',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.2)' }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)' }}
          >
            Bắt đầu học ngay
            <span className="material-symbols-outlined" style={{ fontSize: '1.375rem' }}>play_arrow</span>
          </button>
        </div>
      </main>
    </div>
  )
}
